define('@ember-data/model/-private', ['exports', '@ember/debug', '@ember/object', '@ember-data/store/-private', '@ember/error', '@ember/object/compat', '@ember/runloop', '@ember/utils', '@glimmer/tracking', 'ember', '@ember/array', '@ember/array/proxy', '@ember/object/computed', 'ember-cached-decorator-polyfill', '@ember/object/internals', 'ember-inflector', '@ember/array/mutable', 'rsvp', '@ember/application'], (function (exports, debug, EmberObject, Private, EmberError, compat, runloop, utils, tracking, Ember, EmberArray, ArrayProxy, computed, emberCachedDecoratorPolyfill, internals, emberInflector, MutableArray, rsvp, application) { 'use strict';

  function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

  var EmberObject__default = /*#__PURE__*/_interopDefaultLegacy(EmberObject);
  var Ember__default = /*#__PURE__*/_interopDefaultLegacy(Ember);
  var EmberArray__default = /*#__PURE__*/_interopDefaultLegacy(EmberArray);
  var ArrayProxy__default = /*#__PURE__*/_interopDefaultLegacy(ArrayProxy);
  var MutableArray__default = /*#__PURE__*/_interopDefaultLegacy(MutableArray);

  function isElementDescriptor(args) {
    let [maybeTarget, maybeKey, maybeDesc] = args;
    return (
      // Ensure we have the right number of args
      args.length === 3 && (
      // Make sure the target is a class or object (prototype)
      typeof maybeTarget === 'function' || typeof maybeTarget === 'object' && maybeTarget !== null) &&
      // Make sure the key is a string
      typeof maybeKey === 'string' && (
      // Make sure the descriptor is the right shape
      typeof maybeDesc === 'object' && maybeDesc !== null && 'enumerable' in maybeDesc && 'configurable' in maybeDesc ||
      // TS compatibility
      maybeDesc === undefined)
    );
  }
  function computedMacroWithOptionalParams(fn) {
    {
      return function () {
        for (var _len = arguments.length, maybeDesc = new Array(_len), _key = 0; _key < _len; _key++) {
          maybeDesc[_key] = arguments[_key];
        }
        return isElementDescriptor(maybeDesc) ? fn()(...maybeDesc) : fn(...maybeDesc);
      };
    }
  }

  /**
    @module @ember-data/model
  */

  function getDefaultValue(record, options, key) {
    if (typeof options.defaultValue === 'function') {
      return options.defaultValue.apply(null, arguments);
    } else {
      let defaultValue = options.defaultValue;
      return defaultValue;
    }
  }

  /**
    `attr` defines an attribute on a [Model](/ember-data/release/classes/Model).
    By default, attributes are passed through as-is, however you can specify an
    optional type to have the value automatically transformed.
    Ember Data ships with four basic transform types: `string`, `number`,
    `boolean` and `date`. You can define your own transforms by subclassing
    [Transform](/ember-data/release/classes/Transform).

    Note that you cannot use `attr` to define an attribute of `id`.

    `attr` takes an optional hash as a second parameter, currently
    supported options are:

    - `defaultValue`: Pass a string or a function to be called to set the attribute
    to a default value if and only if the key is absent from the payload response.

    Example

    ```app/models/user.js
    import Model, { attr } from '@ember-data/model';

    export default class UserModel extends Model {
      @attr('string') username;
      @attr('string') email;
      @attr('boolean', { defaultValue: false }) verified;
    }
    ```

    Default value can also be a function. This is useful it you want to return
    a new object for each attribute.

    ```app/models/user.js
    import Model, { attr } from '@ember-data/model';

    export default class UserModel extends Model {
      @attr('string') username;
      @attr('string') email;

      @attr({
        defaultValue() {
          return {};
        }
      })
      settings;
    }
    ```

    The `options` hash is passed as second argument to a transforms'
    `serialize` and `deserialize` method. This allows to configure a
    transformation and adapt the corresponding value, based on the config:

    ```app/models/post.js
    import Model, { attr } from '@ember-data/model';

    export default class PostModel extends Model {
      @attr('text', {
        uppercase: true
      })
      text;
    }
    ```

    ```app/transforms/text.js
    import Transform from '@ember-data/serializer/transform';

    export default class TextTransform extends Transform {
      serialize(value, options) {
        if (options.uppercase) {
          return value.toUpperCase();
        }

        return value;
      }

      deserialize(value) {
        return value;
      }
    }
    ```

    @method attr
    @public
    @static
    @for @ember-data/model
    @param {String|Object} type the attribute type
    @param {Object} options a hash of options
    @return {Attribute}
  */
  function attr(type, options) {
    if (typeof type === 'object') {
      options = type;
      type = undefined;
    } else {
      options = options || {};
    }
    let meta = {
      type: type,
      isAttribute: true,
      kind: 'attribute',
      options: options
    };
    return EmberObject.computed({
      get(key) {
        let recordData = Private.recordDataFor(this);
        if (recordData.hasAttr(key)) {
          return recordData.getAttr(key);
        } else {
          return getDefaultValue(this, options, key);
        }
      },
      set(key, value) {
        if (!this.isValid) {
          let oldValue = this._internalModel._recordData.getAttr(key);
          if (oldValue !== value) {
            const {
              errors
            } = this;
            if (errors.get(key)) {
              errors.remove(key);
              this.___recordState.cleanErrorRequests();
            }
          }
        }
        return this._internalModel.setDirtyAttribute(key, value);
      }
    }).meta(meta);
  }
  var attr$1 = computedMacroWithOptionalParams(attr);

  /**
    @module @ember-data/model
  */

  /**
    `belongsTo` is used to define One-To-One and One-To-Many
    relationships on a [Model](/ember-data/release/classes/Model).


    `belongsTo` takes an optional hash as a second parameter, currently
    supported options are:

    - `async`: A boolean value used to explicitly declare this to be an async relationship. The default is true.
    - `inverse`: A string used to identify the inverse property on a
      related model in a One-To-Many relationship. See [Explicit Inverses](#explicit-inverses)
    - `polymorphic` A boolean value to mark the relationship as polymorphic

    #### One-To-One
    To declare a one-to-one relationship between two models, use
    `belongsTo`:

    ```app/models/user.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class UserModel extends Model {
      @belongsTo('profile') profile;
    }
    ```

    ```app/models/profile.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class ProfileModel extends Model {
      @belongsTo('user') user;
    }
    ```

    #### One-To-Many
    To declare a one-to-many relationship between two models, use
    `belongsTo` in combination with `hasMany`, like this:

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany('comment') comments;
    }
    ```

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo('post') post;
    }
    ```

    You can avoid passing a string as the first parameter. In that case Ember Data
    will infer the type from the key name.

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo post;
    }
    ```

    will lookup for a Post type.

    #### Sync relationships

    Ember Data resolves sync relationships with the related resources
    available in its local store, hence it is expected these resources
    to be loaded before or along-side the primary resource.

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo('post', {
        async: false
      })
      post;
    }
    ```

    In contrast to async relationship, accessing a sync relationship
    will always return the record (Model instance) for the existing
    local resource, or null. But it will error on access when
    a related resource is known to exist and it has not been loaded.

    ```
    let post = comment.get('post');

    ```

    @method belongsTo
    @public
    @static
    @for @ember-data/model
    @param {String} modelName (optional) type of the relationship
    @param {Object} options (optional) a hash of options
    @return {Ember.computed} relationship
  */
  function belongsTo(modelName, options) {
    let opts, userEnteredModelName;
    if (typeof modelName === 'object') {
      opts = modelName;
      userEnteredModelName = undefined;
    } else {
      opts = options;
      userEnteredModelName = modelName;
    }
    opts = opts || {};
    if (!('async' in opts)) {
      opts.async = true;
    }
    let meta = {
      type: userEnteredModelName,
      isRelationship: true,
      options: opts,
      kind: 'belongsTo',
      name: 'Belongs To',
      key: null
    };
    return EmberObject.computed({
      get(key) {
        return this._internalModel.getBelongsTo(key);
      },
      set(key, value) {
        this.store._backburner.join(() => {
          this._internalModel.setDirtyBelongsTo(key, value);
        });
        return this._internalModel.getBelongsTo(key);
      }
    }).meta(meta);
  }
  var belongsTo$1 = computedMacroWithOptionalParams(belongsTo);

  /**
    `hasMany` is used to define One-To-Many and Many-To-Many
    relationships on a [Model](/ember-data/release/classes/Model).

    `hasMany` takes an optional hash as a second parameter, currently
    supported options are:

    - `async`: A boolean value used to explicitly declare this to be an async relationship. The default is true.
    - `inverse`: A string used to identify the inverse property on a related model.
    - `polymorphic` A boolean value to mark the relationship as polymorphic

    #### One-To-Many
    To declare a one-to-many relationship between two models, use
    `belongsTo` in combination with `hasMany`, like this:

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany('comment') comments;
    }
    ```

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo('post') post;
    }
    ```

    #### Many-To-Many
    To declare a many-to-many relationship between two models, use
    `hasMany`:

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany('tag') tags;
    }
    ```

    ```app/models/tag.js
    import Model, { hasMany } from '@ember-data/model';

    export default class TagModel extends Model {
      @hasMany('post') posts;
    }
    ```

    You can avoid passing a string as the first parameter. In that case Ember Data
    will infer the type from the singularized key name.

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany tags;
    }
    ```

    will lookup for a Tag type.

    #### Explicit Inverses

    Ember Data will do its best to discover which relationships map to
    one another. In the one-to-many code above, for example, Ember Data
    can figure out that changing the `comments` relationship should update
    the `post` relationship on the inverse because post is the only
    relationship to that model.

    However, sometimes you may have multiple `belongsTo`/`hasMany` for the
    same type. You can specify which property on the related model is
    the inverse using `hasMany`'s `inverse` option:

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo('post') onePost;
      @belongsTo('post') twoPost
      @belongsTo('post') redPost;
      @belongsTo('post') bluePost;
    }
    ```

    ```app/models/post.js
    import Model from '@ember-data/model';
    import { hasMany } from '@ember-decorators/data';

    export default class PostModel extends Model {
      @hasMany('comment', {
        inverse: 'redPost'
      })
      comments;
    }
    ```

    You can also specify an inverse on a `belongsTo`, which works how
    you'd expect.

    #### Sync relationships

    Ember Data resolves sync relationships with the related resources
    available in its local store, hence it is expected these resources
    to be loaded before or along-side the primary resource.

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany('comment', {
        async: false
      })
      comments;
    }
    ```

    In contrast to async relationship, accessing a sync relationship
    will always return a [ManyArray](/ember-data/release/classes/ManyArray) instance
    containing the existing local resources. But it will error on access
    when any of the known related resources have not been loaded.

    ```
    post.get('comments').forEach((comment) => {

    });

    ```

    If you are using `links` with sync relationships, you have to use
    `ref.reload` to fetch the resources.

    @method hasMany
    @public
    @static
    @for @ember-data/model
    @param {String} type (optional) type of the relationship
    @param {Object} options (optional) a hash of options
    @return {Ember.computed} relationship
  */
  function hasMany(type, options) {
    if (typeof type === 'object') {
      options = type;
      type = undefined;
    }
    options = options || {};
    if (!('async' in options)) {
      options.async = true;
    }

    // Metadata about relationships is stored on the meta of
    // the relationship. This is used for introspection and
    // serialization. Note that `key` is populated lazily
    // the first time the CP is called.
    let meta = {
      type,
      options,
      isRelationship: true,
      kind: 'hasMany',
      name: 'Has Many',
      key: null
    };
    return EmberObject.computed({
      get(key) {
        return this._internalModel.getHasMany(key);
      },
      set(key, records) {
        let internalModel = this._internalModel;
        this.store._backburner.join(() => {
          internalModel.setDirtyHasMany(key, records);
        });
        return internalModel.getHasMany(key);
      }
    }).meta(meta);
  }
  var hasMany$1 = computedMacroWithOptionalParams(hasMany);

  /**
    @module @ember-data/store
  */

  /**
    Holds validation errors for a given record, organized by attribute names.

    This class is not directly instantiable.

    Every `Model` has an `errors` property that is an instance of
    `Errors`. This can be used to display validation error
    messages returned from the server when a `record.save()` rejects.

    For Example, if you had a `User` model that looked like this:

    ```app/models/user.js
    import Model, { attr } from '@ember-data/model';

    export default class UserModel extends Model {
      @attr('string') username;
      @attr('string') email;
    }
    ```
    And you attempted to save a record that did not validate on the backend:

    ```javascript
    let user = store.createRecord('user', {
      username: 'tomster',
      email: 'invalidEmail'
    });
    user.save();
    ```

    Your backend would be expected to return an error response that described
    the problem, so that error messages can be generated on the app.

    API responses will be translated into instances of `Errors` differently,
    depending on the specific combination of adapter and serializer used. You
    may want to check the documentation or the source code of the libraries
    that you are using, to know how they expect errors to be communicated.

    Errors can be displayed to the user by accessing their property name
    to get an array of all the error objects for that property. Each
    error object is a JavaScript object with two keys:

    - `message` A string containing the error message from the backend
    - `attribute` The name of the property associated with this error message

    ```handlebars
    <label>Username: <Input @value={{@model.username}} /> </label>
    {{#each @model.errors.username as |error|}}
      <div class="error">
        {{error.message}}
      </div>
    {{/each}}

    <label>Email: <Input @value={{@model.email}} /> </label>
    {{#each @model.errors.email as |error|}}
      <div class="error">
        {{error.message}}
      </div>
    {{/each}}
    ```

    You can also access the special `messages` property on the error
    object to get an array of all the error strings.

    ```handlebars
    {{#each @model.errors.messages as |message|}}
      <div class="error">
        {{message}}
      </div>
    {{/each}}
    ```

    @class Errors
    @public
    @extends Ember.ArrayProxy
   */
  var Errors = ArrayProxy__default["default"].extend({
    /**
      Register with target handler
       @method _registerHandlers
      @private
    */
    _registerHandlers(becameInvalid, becameValid) {
      this._registeredHandlers = {
        becameInvalid,
        becameValid
      };
    },
    /**
      @property errorsByAttributeName
      @type {MapWithDefault}
      @private
    */
    errorsByAttributeName: EmberObject.computed(function () {
      return new Map();
    }),
    /**
      Returns errors for a given attribute
       ```javascript
      let user = store.createRecord('user', {
        username: 'tomster',
        email: 'invalidEmail'
      });
      user.save().catch(function(){
        user.get('errors').errorsFor('email'); // returns:
        // [{attribute: "email", message: "Doesn't look like a valid email."}]
      });
      ```
       @method errorsFor
      @public
      @param {String} attribute
      @return {Array}
    */
    errorsFor(attribute) {
      let map = EmberObject.get(this, 'errorsByAttributeName');
      let errors = map.get(attribute);
      if (errors === undefined) {
        errors = EmberArray.A();
        map.set(attribute, errors);
      }

      // Errors may be a native array with extensions turned on. Since we access
      // the array via a method, and not a computed or using `Ember.get`, it does
      // not entangle properly with autotracking, so we entangle manually by
      // getting the `[]` property.
      EmberObject.get(errors, '[]');
      return errors;
    },
    /**
      An array containing all of the error messages for this
      record. This is useful for displaying all errors to the user.
       ```handlebars
      {{#each @model.errors.messages as |message|}}
        <div class="error">
          {{message}}
        </div>
      {{/each}}
      ```
       @property messages
      @public
      @type {Array}
    */
    messages: computed.mapBy('content', 'message'),
    /**
      @property content
      @type {Array}
      @private
    */
    content: EmberObject.computed(function () {
      return EmberArray.A();
    }),
    /**
      @method unknownProperty
      @private
    */
    unknownProperty(attribute) {
      let errors = this.errorsFor(attribute);
      if (errors.length === 0) {
        return undefined;
      }
      return errors;
    },
    /**
      Total number of errors.
       @property length
      @type {Number}
      @public
      @readOnly
    */

    /**
      `true` if we have no errors.
       @property isEmpty
      @type {Boolean}
      @public
      @readOnly
    */
    isEmpty: computed.not('length').readOnly(),
    /**
     Manually adds errors to the record. This will trigger the `becameInvalid` event/ lifecycle method on
      the record and transition the record into an `invalid` state.
      Example
     ```javascript
      let errors = get(user, 'errors');
       // add multiple errors
      errors.add('password', [
        'Must be at least 12 characters',
        'Must contain at least one symbol',
        'Cannot contain your name'
      ]);
       errors.errorsFor('password');
      // =>
      // [
      //   { attribute: 'password', message: 'Must be at least 12 characters' },
      //   { attribute: 'password', message: 'Must contain at least one symbol' },
      //   { attribute: 'password', message: 'Cannot contain your name' },
      // ]
       // add a single error
      errors.add('username', 'This field is required');
       errors.errorsFor('username');
      // =>
      // [
      //   { attribute: 'username', message: 'This field is required' },
      // ]
     ```
    @method add
      @public
    @param {string} attribute - the property name of an attribute or relationship
    @param {string[]|string} messages - an error message or array of error messages for the attribute
     */
    add(attribute, messages) {
      let wasEmpty = EmberObject.get(this, 'isEmpty');
      this._add(attribute, messages);
      if (wasEmpty && !EmberObject.get(this, 'isEmpty')) {
        this._registeredHandlers && this._registeredHandlers.becameInvalid();
      }
    },
    /**
      Adds error messages to a given attribute without sending event.
       @method _add
      @private
    */
    _add(attribute, messages) {
      messages = this._findOrCreateMessages(attribute, messages);
      this.addObjects(messages);
      this.errorsFor(attribute).addObjects(messages);
      this.notifyPropertyChange(attribute);
    },
    /**
      @method _findOrCreateMessages
      @private
    */
    _findOrCreateMessages(attribute, messages) {
      let errors = this.errorsFor(attribute);
      let messagesArray = EmberArray.makeArray(messages);
      let _messages = new Array(messagesArray.length);
      for (let i = 0; i < messagesArray.length; i++) {
        let message = messagesArray[i];
        let err = errors.findBy('message', message);
        if (err) {
          _messages[i] = err;
        } else {
          _messages[i] = {
            attribute: attribute,
            message: message
          };
        }
      }
      return _messages;
    },
    /**
     Manually removes all errors for a given member from the record.
       This will transition the record into a `valid` state, and
      triggers the `becameValid` event and lifecycle method.
      Example:
      ```javascript
      let errors = get('user', errors);
      errors.add('phone', ['error-1', 'error-2']);
       errors.errorsFor('phone');
      // =>
      // [
      //   { attribute: 'phone', message: 'error-1' },
      //   { attribute: 'phone', message: 'error-2' },
      // ]
       errors.remove('phone');
       errors.errorsFor('phone');
      // => undefined
     ```
     @method remove
      @public
     @param {string} member - the property name of an attribute or relationship
     */
    remove(attribute) {
      if (EmberObject.get(this, 'isEmpty')) {
        return;
      }
      this._remove(attribute);
      if (EmberObject.get(this, 'isEmpty')) {
        this._registeredHandlers && this._registeredHandlers.becameValid();
      }
    },
    /**
      Removes all error messages from the given attribute without sending event.
       @method _remove
      @private
    */
    _remove(attribute) {
      if (EmberObject.get(this, 'isEmpty')) {
        return;
      }
      let content = this.rejectBy('attribute', attribute);
      EmberObject.get(this, 'content').setObjects(content);

      // Although errorsByAttributeName.delete is technically enough to sync errors state, we also
      // must mutate the array as well for autotracking
      let errors = this.errorsFor(attribute);
      for (let i = 0; i < errors.length; i++) {
        if (errors[i].attribute === attribute) {
          // .replace from Ember.NativeArray is necessary. JS splice will not work.
          errors.replace(i, 1);
        }
      }
      EmberObject.get(this, 'errorsByAttributeName').delete(attribute);
      this.notifyPropertyChange(attribute);
      this.notifyPropertyChange('length');
    },
    /**
     Manually clears all errors for the record.
       This will transition the record into a `valid` state, and
       will trigger the `becameValid` event and lifecycle method.
     Example:
      ```javascript
     let errors = get('user', errors);
     errors.add('username', ['error-a']);
     errors.add('phone', ['error-1', 'error-2']);
      errors.errorsFor('username');
     // =>
     // [
     //   { attribute: 'username', message: 'error-a' },
     // ]
      errors.errorsFor('phone');
     // =>
     // [
     //   { attribute: 'phone', message: 'error-1' },
     //   { attribute: 'phone', message: 'error-2' },
     // ]
      errors.clear();
      errors.errorsFor('username');
     // => undefined
      errors.errorsFor('phone');
     // => undefined
      errors.get('messages')
     // => []
     ```
     @method clear
      @public
     */
    clear() {
      if (EmberObject.get(this, 'isEmpty')) {
        return;
      }
      this._clear();
      this._registeredHandlers && this._registeredHandlers.becameValid();
    },
    /**
      Removes all error messages.
      to the record.
       @method _clear
      @private
    */
    _clear() {
      if (EmberObject.get(this, 'isEmpty')) {
        return;
      }
      let errorsByAttributeName = EmberObject.get(this, 'errorsByAttributeName');
      let attributes = [];
      errorsByAttributeName.forEach(function (_, attribute) {
        attributes.push(attribute);
      });
      errorsByAttributeName.clear();
      attributes.forEach(attribute => {
        this.notifyPropertyChange(attribute);
      });
      ArrayProxy__default["default"].prototype.clear.call(this);
    },
    /**
      Checks if there are error messages for the given attribute.
       ```app/controllers/user/edit.js
      import Controller from '@ember/controller';
      import { action } from '@ember/object';
       export default class UserEditController extends Controller {
        @action
        save(user) {
          if (user.get('errors').has('email')) {
            return alert('Please update your email before attempting to save.');
          }
          user.save();
        }
      }
      ```
       @method has
      @public
      @param {String} attribute
      @return {Boolean} true if there some errors on given attribute
    */
    has(attribute) {
      return this.errorsFor(attribute).length > 0;
    }
  });

  function notifyChanges(identifier, value, key, record, store) {
    if (value === 'attributes') {
      if (key) {
        notifyAttribute(store, identifier, key, record);
      } else {
        record.eachAttribute(key => {
          notifyAttribute(store, identifier, key, record);
        });
      }
    } else if (value === 'relationships') {
      if (key) {
        let meta = record.constructor.relationshipsByName.get(key);
        notifyRelationship(store, identifier, key, record, meta);
      } else {
        record.eachRelationship((key, meta) => {
          notifyRelationship(store, identifier, key, record, meta);
        });
      }
    } else if (value === 'identity') {
      record.notifyPropertyChange('id');
    }
  }
  function notifyRelationship(store, identifier, key, record, meta) {
    let internalModel = store._internalModelForResource(identifier);
    if (meta.kind === 'belongsTo') {
      record.notifyPropertyChange(key);
    } else if (meta.kind === 'hasMany') {
      let manyArray = internalModel._manyArrayCache[key];
      if (manyArray) {
        manyArray.notify();

        //We need to notifyPropertyChange in the adding case because we need to make sure
        //we fetch the newly added record in case it is unloaded
        //TODO(Igor): Consider whether we could do this only if the record state is unloaded
        if (!meta.options || meta.options.async || meta.options.async === undefined) {
          record.notifyPropertyChange(key);
        }
      }
    }
  }
  function notifyAttribute(store, identifier, key, record) {
    let currentValue = internals.cacheFor(record, key);
    let internalModel = store._internalModelForResource(identifier);
    if (currentValue !== internalModel._recordData.getAttr(key)) {
      record.notifyPropertyChange(key);
    }
  }

  var _class$2, _descriptor$2, _class3, _descriptor2$1;
  function _initializerDefineProperty$2(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _applyDecoratedDescriptor$2(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
  function isInvalidError(error) {
    return error && error.isAdapterError === true && error.code === 'InvalidError';
  }

  /**
   * Tag provides a cache for a getter
   * that recomputes only when a specific
   * tracked property that it manages is dirtied.
   *
   * This allows us to bust the cache for a value
   * that otherwise doesn't access anything tracked
   * as well as control the timing of that notification.
   *
   * @internal
   */
  let Tag = (_class$2 = class Tag {
    constructor() {
      _initializerDefineProperty$2(this, "_tracking", _descriptor$2, this);
      this.rev = 1;
      this.isDirty = true;
      this.value = undefined;
    }
    subscribe() {
      this._tracking;
    }
    notify() {
      this.isDirty = true;
      this._tracking = this.rev;
      this.rev++;
    }
    consume(v) {
      this.isDirty = false;
      this.value = v; // set cached value
    }
  }, (_descriptor$2 = _applyDecoratedDescriptor$2(_class$2.prototype, "_tracking", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return 0;
    }
  })), _class$2);
  const Tags = new WeakMap();
  function getTag(record, key) {
    let tags = Tags.get(record);
    if (!tags) {
      tags = Object.create(null);
      Tags.set(record, tags);
    }
    return tags[key] = tags[key] || new Tag();
  }
  function peekTag(record, key) {
    let tags = Tags.get(record);
    return tags && tags[key];
  }

  /**
   * A decorattor that caches a getter while
   * providing the ability to bust that cache
   * when we so choose in a way that notifies
   * glimmer's tracking system.
   *
   * @internal
   */
  function tagged(_target, key, desc) {
    const getter = desc.get;
    const setter = desc.set;
    desc.get = function () {
      let tag = getTag(this, key);
      tag.subscribe();
      if (tag.isDirty) {
        tag.consume(getter.call(this));
      }
      return tag.value;
    };
    desc.set = function (v) {
      getTag(this, key); // ensure tag is setup in case we want to use it.
      // probably notify here but not yet.
      setter.call(this, v);
    };
    compat.dependentKeyCompat(desc);
    return desc;
  }

  /**
  Historically InternalModel managed a state machine
  the currentState for which was reflected onto Model.

  This implements the flags and stateName for backwards compat
  with the state tree that used to be possible (listed below).

  stateName and dirtyType are candidates for deprecation.

  root
    empty
      deleted    // hidden from stateName
      preloaded  // hidden from stateName

    loading
       empty     // hidden from stateName
       preloaded // hidden from stateName

    loaded
      saved
      updated
        uncommitted
        invalid
        inFlight
      created
        uncommitted
        invalid
        inFlight

    deleted
      saved
        new      // hidden from stateName
      uncommitted
      invalid
      inFlight

    @internal
  */
  let RecordState = (_class3 = class RecordState {
    constructor(record) {
      _initializerDefineProperty$2(this, "isSaving", _descriptor2$1, this);
      const {
        store
      } = record;
      let id = record._internalModel.identifier;
      this.record = record;
      this.recordData = record._internalModel._recordData;
      this.pendingCount = 0;
      this.fulfilledCount = 0;
      this.rejectedCount = 0;
      this._errorRequests = [];
      this._lastError = null;
      let requests = store.getRequestStateService();
      let notifications = store._notificationManager;
      requests.subscribeForRecord(id, req => {
        if (req.type === 'mutation') {
          switch (req.state) {
            case 'pending':
              this.isSaving = true;
              break;
            case 'rejected':
              this.isSaving = false;
              this._lastError = req;
              if (!(req.response && isInvalidError(req.response.data))) {
                this._errorRequests.push(req);
              }
              notifyErrorsStateChanged(this);
              break;
            case 'fulfilled':
              this._errorRequests = [];
              this._lastError = null;
              this.isSaving = false;
              notifyErrorsStateChanged(this);
              break;
          }
        } else {
          switch (req.state) {
            case 'pending':
              this.pendingCount++;
              this.notify('isLoading');
              break;
            case 'rejected':
              this.pendingCount--;
              this._lastError = req;
              if (!(req.response && isInvalidError(req.response.data))) {
                this._errorRequests.push(req);
              }
              this.notify('isLoading');
              notifyErrorsStateChanged(this);
              break;
            case 'fulfilled':
              this.pendingCount--;
              this.fulfilledCount++;
              this.notify('isLoading');
              this.notify('isDirty');
              notifyErrorsStateChanged(this);
              this._errorRequests = [];
              this._lastError = null;
              break;
          }
        }
      });
      notifications.subscribe(id, (identifier, type, key) => {
        notifyChanges(identifier, type, key, record, store);
        switch (type) {
          case 'state':
            this.notify('isNew');
            this.notify('isDeleted');
            this.notify('isDirty');
            break;
          case 'attributes':
            this.notify('isEmpty');
            this.notify('isDirty');
            break;
          case 'unload':
            this.notify('isNew');
            this.notify('isDeleted');
            break;
          case 'errors':
            // we only hit this if a foreign RecordData notifies
            // errors changed. Our own implementation does not
            // take this path currently, but we should probably
            // fix that.
            this.updateInvalidErrors();
            this.notify('isValid');
            break;
        }
      });
    }
    notify(key) {
      getTag(this, key).notify();
    }
    updateInvalidErrors() {
      let jsonApiErrors = this.recordData.getErrors();
      const {
        errors
      } = this.record;
      errors._clear();
      let newErrors = Private.errorsArrayToHash(jsonApiErrors);
      let errorKeys = Object.keys(newErrors);
      for (let i = 0; i < errorKeys.length; i++) {
        errors._add(errorKeys[i], newErrors[errorKeys[i]]);
      }
    }
    cleanErrorRequests() {
      this.notify('isValid');
      this.notify('isError');
      this.notify('adapterError');
      this._errorRequests = [];
      this._lastError = null;
    }
    get isLoading() {
      return !this.isLoaded && this.pendingCount > 0 && this.fulfilledCount === 0;
    }
    get isLoaded() {
      if (this.isNew) {
        return true;
      }
      return this.fulfilledCount > 0 || !this.isEmpty;
    }
    get isSaved() {
      let rd = this.recordData;
      if (this.isDeleted) {
        return rd.isDeletionCommitted();
      }
      if (this.isNew || this.isEmpty || !this.isValid || this.isDirty || this.isLoading) {
        return false;
      }
      return true;
    }
    get isEmpty() {
      let rd = this.recordData;
      return !this.isNew && rd.isEmpty();
    }
    get isNew() {
      let rd = this.recordData;
      return rd.isNew();
    }
    get isDeleted() {
      let rd = this.recordData;
      return rd.isDeleted();
    }
    get isValid() {
      return this.record.errors.length === 0;
    }
    get isDirty() {
      let rd = this.recordData;
      if (rd.isDeletionCommitted() || this.isDeleted && this.isNew) {
        return false;
      }
      return this.isNew || rd.hasChangedAttributes();
    }
    get isError() {
      let errorReq = this._errorRequests[this._errorRequests.length - 1];
      if (!errorReq) {
        return false;
      } else {
        return true;
      }
    }
    get adapterError() {
      let request = this._lastError;
      if (!request) {
        return null;
      }
      return request.state === 'rejected' && request.response.data;
    }
    get isPreloaded() {
      return !this.isEmpty && this.isLoading;
    }
    get stateName() {
      // we might be empty while loading so check this first
      if (this.isLoading) {
        return 'root.loading';

        // got nothing yet or were unloaded
      } else if (this.isEmpty) {
        return 'root.empty';

        // deleted substates
      } else if (this.isDeleted) {
        if (this.isSaving) {
          return 'root.deleted.inFlight';
        } else if (this.isSaved) {
          // TODO ensure isSaved isn't true from previous requests
          return 'root.deleted.saved';
        } else if (!this.isValid) {
          return 'root.deleted.invalid';
        } else {
          return 'root.deleted.uncommitted';
        }

        // loaded.created substates
      } else if (this.isNew) {
        if (this.isSaving) {
          return 'root.loaded.created.inFlight';
        } else if (!this.isValid) {
          return 'root.loaded.created.invalid';
        }
        return 'root.loaded.created.uncommitted';

        // loaded.updated substates
      } else if (this.isSaving) {
        return 'root.loaded.updated.inFlight';
      } else if (!this.isValid) {
        return 'root.loaded.updated.invalid';
      } else if (this.isDirty) {
        return 'root.loaded.updated.uncommitted';

        // if nothing remains, we are loaded saved!
      } else {
        return 'root.loaded.saved';
      }
    }
    get dirtyType() {
      // we might be empty while loading so check this first
      if (this.isLoading || this.isEmpty) {
        return '';

        // deleted substates
      } else if (this.isDeleted) {
        return 'deleted';

        // loaded.created substates
      } else if (this.isNew) {
        return 'created';

        // loaded.updated substates
      } else if (this.isSaving || !this.isValid || this.isDirty) {
        return 'updated';

        // if nothing remains, we are loaded saved!
      } else {
        return '';
      }
    }
  }, (_descriptor2$1 = _applyDecoratedDescriptor$2(_class3.prototype, "isSaving", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _applyDecoratedDescriptor$2(_class3.prototype, "isLoading", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isLoading"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isLoaded", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isLoaded"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isSaved", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isSaved"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isEmpty", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isEmpty"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isNew", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isNew"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isDeleted", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isDeleted"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isValid", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isValid"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isDirty", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isDirty"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isError", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "isError"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "adapterError", [tagged], Object.getOwnPropertyDescriptor(_class3.prototype, "adapterError"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "isPreloaded", [emberCachedDecoratorPolyfill.cached], Object.getOwnPropertyDescriptor(_class3.prototype, "isPreloaded"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "stateName", [emberCachedDecoratorPolyfill.cached], Object.getOwnPropertyDescriptor(_class3.prototype, "stateName"), _class3.prototype), _applyDecoratedDescriptor$2(_class3.prototype, "dirtyType", [emberCachedDecoratorPolyfill.cached], Object.getOwnPropertyDescriptor(_class3.prototype, "dirtyType"), _class3.prototype)), _class3);
  function notifyErrorsStateChanged(state) {
    state.notify('isValid');
    state.notify('isError');
    state.notify('adapterError');
  }

  /**
    @module @ember-data/store
  */

  function typeForRelationshipMeta(meta) {
    let modelName = Private.normalizeModelName(meta.type || meta.key);
    if (meta.kind === 'hasMany') {
      modelName = emberInflector.singularize(modelName);
    }
    return modelName;
  }
  function shouldFindInverse(relationshipMeta) {
    let options = relationshipMeta.options;
    return !(options && options.inverse === null);
  }
  class RelationshipDefinition {
    constructor(meta) {
      this._type = '';
      this.__inverseKey = '';
      this.__inverseIsAsync = true;
      this.__hasCalculatedInverse = false;
      this.parentModelName = meta.parentModelName;
      this.meta = meta;
    }

    /**
     * @internal
     * @deprecated
     */
    get key() {
      return this.meta.key;
    }
    get kind() {
      return this.meta.kind;
    }
    get type() {
      if (this._type) {
        return this._type;
      }
      this._type = typeForRelationshipMeta(this.meta);
      return this._type;
    }
    get options() {
      return this.meta.options;
    }
    get name() {
      return this.meta.name;
    }
    _inverseKey(store, modelClass) {
      if (this.__hasCalculatedInverse === false) {
        this._calculateInverse(store, modelClass);
      }
      return this.__inverseKey;
    }
    _inverseIsAsync(store, modelClass) {
      if (this.__hasCalculatedInverse === false) {
        this._calculateInverse(store, modelClass);
      }
      return this.__inverseIsAsync;
    }
    _calculateInverse(store, modelClass) {
      this.__hasCalculatedInverse = true;
      let inverseKey, inverseIsAsync;
      let inverse = null;
      if (shouldFindInverse(this.meta)) {
        inverse = modelClass.inverseFor(this.key, store);
      }
      if (inverse) {
        inverseKey = inverse.name;
        inverseIsAsync = isRelationshipAsync(inverse);
      } else {
        inverseKey = null;
        inverseIsAsync = false;
      }
      this.__inverseKey = inverseKey;
      this.__inverseIsAsync = inverseIsAsync;
    }
  }
  function isRelationshipAsync(meta) {
    let inverseAsync = meta.options && meta.options.async;
    return typeof inverseAsync === 'undefined' ? true : inverseAsync;
  }
  function relationshipFromMeta(meta) {
    return new RelationshipDefinition(meta);
  }

  var _class$1, _descriptor$1, _class2;
  function _initializerDefineProperty$1(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _applyDecoratedDescriptor$1(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
  const {
    changeProperties
  } = Ember__default["default"];
  function findPossibleInverses(type, inverseType, name, relationshipsSoFar) {
    let possibleRelationships = relationshipsSoFar || [];
    let relationshipMap = inverseType.relationships;
    if (!relationshipMap) {
      return possibleRelationships;
    }
    let relationshipsForType = relationshipMap.get(type.modelName);
    let relationships = Array.isArray(relationshipsForType) ? relationshipsForType.filter(relationship => {
      let optionsForRelationship = inverseType.metaForProperty(relationship.name).options;
      if (!optionsForRelationship.inverse && optionsForRelationship.inverse !== null) {
        return true;
      }
      return name === optionsForRelationship.inverse;
    }) : null;
    if (relationships) {
      possibleRelationships.push.apply(possibleRelationships, relationships);
    }

    //Recurse to support polymorphism
    if (type.superclass) {
      findPossibleInverses(type.superclass, inverseType, name, possibleRelationships);
    }
    return possibleRelationships;
  }

  /*
   * This decorator allows us to lazily compute
   * an expensive getter on first-access and thereafter
   * never recompute it.
   */
  function computeOnce(target, key, desc) {
    const cache = new WeakMap();
    let getter = desc.get;
    desc.get = function () {
      let meta = cache.get(this);
      if (!meta) {
        meta = {
          hasComputed: false,
          value: undefined
        };
        cache.set(this, meta);
      }
      if (!meta.hasComputed) {
        meta.value = getter.call(this);
        meta.hasComputed = true;
      }
      return meta.value;
    };
    return desc;
  }

  /**
    Base class from which Models can be define.

    ```js
    import Model, { attr } from '@ember-data/model';

    export default class User extends Model {
      @attr name;
    }
    ```

    @class Model
    @public
    @extends Ember.EmberObject
  */
  let Model = (_class$1 = (_class2 = class Model extends EmberObject__default["default"] {
    constructor() {
      super(...arguments);
      /**
        If `true` the store is attempting to reload the record from the adapter.
         Example
         ```javascript
        record.get('isReloading'); // false
        record.reload();
        record.get('isReloading'); // true
        ```
         @property isReloading
        @public
        @type {Boolean}
        @readOnly
      */
      _initializerDefineProperty$1(this, "isReloading", _descriptor$1, this);
    }
    init(options) {
      if (options === void 0) {
        options = {};
      }
      const createProps = options._createProps;
      delete options._createProps;
      super.init(options);
      this.___recordState = new RecordState(this);
      this.setProperties(createProps);
    }

    /**
      If this property is `true` the record is in the `empty`
      state. Empty is the first state all records enter after they have
      been created. Most records created by the store will quickly
      transition to the `loading` state if data needs to be fetched from
      the server or the `created` state if the record is created on the
      client. A record can also enter the empty state if the adapter is
      unable to locate the record.
       @property isEmpty
      @public
      @type {Boolean}
      @readOnly
    */
    get isEmpty() {
      return this.currentState.isEmpty;
    }

    /**
      If this property is `true` the record is in the `loading` state. A
      record enters this state when the store asks the adapter for its
      data. It remains in this state until the adapter provides the
      requested data.
       @property isLoading
      @public
      @type {Boolean}
      @readOnly
    */
    get isLoading() {
      return this.currentState.isLoading;
    }

    /**
      If this property is `true` the record is in the `loaded` state. A
      record enters this state when its data is populated. Most of a
      record's lifecycle is spent inside substates of the `loaded`
      state.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('isLoaded'); // true
       store.findRecord('model', 1).then(function(model) {
        model.get('isLoaded'); // true
      });
      ```
       @property isLoaded
      @public
      @type {Boolean}
      @readOnly
    */
    get isLoaded() {
      return this.currentState.isLoaded;
    }

    /**
      If this property is `true` the record is in the `dirty` state. The
      record has local changes that have not yet been saved by the
      adapter. This includes records that have been created (but not yet
      saved) or deleted.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('hasDirtyAttributes'); // true
       store.findRecord('model', 1).then(function(model) {
        model.get('hasDirtyAttributes'); // false
        model.set('foo', 'some value');
        model.get('hasDirtyAttributes'); // true
      });
      ```
       @since 1.13.0
      @property hasDirtyAttributes
      @public
      @type {Boolean}
      @readOnly
    */
    get hasDirtyAttributes() {
      return this.currentState.isDirty;
    }

    /**
      If this property is `true` the record is in the `saving` state. A
      record enters the saving state when `save` is called, but the
      adapter has not yet acknowledged that the changes have been
      persisted to the backend.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('isSaving'); // false
      let promise = record.save();
      record.get('isSaving'); // true
      promise.then(function() {
        record.get('isSaving'); // false
      });
      ```
       @property isSaving
      @public
      @type {Boolean}
      @readOnly
    */
    get isSaving() {
      return this.currentState.isSaving;
    }

    /**
      If this property is `true` the record is in the `deleted` state
      and has been marked for deletion. When `isDeleted` is true and
      `hasDirtyAttributes` is true, the record is deleted locally but the deletion
      was not yet persisted. When `isSaving` is true, the change is
      in-flight. When both `hasDirtyAttributes` and `isSaving` are false, the
      change has persisted.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('isDeleted');    // false
      record.deleteRecord();
       // Locally deleted
      record.get('isDeleted');           // true
      record.get('hasDirtyAttributes');  // true
      record.get('isSaving');            // false
       // Persisting the deletion
      let promise = record.save();
      record.get('isDeleted');    // true
      record.get('isSaving');     // true
       // Deletion Persisted
      promise.then(function() {
        record.get('isDeleted');          // true
        record.get('isSaving');           // false
        record.get('hasDirtyAttributes'); // false
      });
      ```
       @property isDeleted
      @public
      @type {Boolean}
      @readOnly
    */
    get isDeleted() {
      return this.currentState.isDeleted;
    }

    /**
      If this property is `true` the record is in the `new` state. A
      record will be in the `new` state when it has been created on the
      client and the adapter has not yet report that it was successfully
      saved.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('isNew'); // true
       record.save().then(function(model) {
        model.get('isNew'); // false
      });
      ```
       @property isNew
      @public
      @type {Boolean}
      @readOnly
    */
    get isNew() {
      return this.currentState.isNew;
    }

    /**
      If this property is `true` the record is in the `valid` state.
       A record will be in the `valid` state when the adapter did not report any
      server-side validation failures.
       @property isValid
      @public
      @type {Boolean}
      @readOnly
    */
    get isValid() {
      return this.currentState.isValid;
    }

    /**
      If the record is in the dirty state this property will report what
      kind of change has caused it to move into the dirty
      state. Possible values are:
       - `created` The record has been created by the client and not yet saved to the adapter.
      - `updated` The record has been updated by the client and not yet saved to the adapter.
      - `deleted` The record has been deleted by the client and not yet saved to the adapter.
       Example
       ```javascript
      let record = store.createRecord('model');
      record.get('dirtyType'); // 'created'
      ```
       @property dirtyType
      @public
      @type {String}
      @readOnly
    */
    get dirtyType() {
      return this.currentState.dirtyType;
    }

    /**
      If `true` the adapter reported that it was unable to save local
      changes to the backend for any reason other than a server-side
      validation error.
       Example
       ```javascript
      record.get('isError'); // false
      record.set('foo', 'valid value');
      record.save().then(null, function() {
        record.get('isError'); // true
      });
      ```
       @property isError
      @public
      @type {Boolean}
      @readOnly
    */
    get isError() {
      return this.currentState.isError;
    }
    set isError(v) {
    }
    /**
      All ember models have an id property. This is an identifier
      managed by an external source. These are always coerced to be
      strings before being used internally. Note when declaring the
      attributes for a model it is an error to declare an id
      attribute.
       ```javascript
      let record = store.createRecord('model');
      record.get('id'); // null
       store.findRecord('model', 1).then(function(model) {
        model.get('id'); // '1'
      });
      ```
       @property id
      @public
      @type {String}
    */
    get id() {
      return this._internalModel.id;
    }
    set id(id) {
      const normalizedId = Private.coerceId(id);
      if (normalizedId !== null) {
        this._internalModel.setId(normalizedId);
      }
    }

    /**
      @property currentState
      @private
      @type {Object}
    */
    get currentState() {
      return this.___recordState;
    }
    set currentState(_v) {
      throw new Error('cannot set currentState');
    }

    /**
     @property _internalModel
     @private
     @type {Object}
     */

    /**
      The store service instance which created this record instance
      @property store
      @public
     */

    /**
      When the record is in the `invalid` state this object will contain
      any errors returned by the adapter. When present the errors hash
      contains keys corresponding to the invalid property names
      and values which are arrays of Javascript objects with two keys:
       - `message` A string containing the error message from the backend
      - `attribute` The name of the property associated with this error message
       ```javascript
      record.get('errors.length'); // 0
      record.set('foo', 'invalid value');
      record.save().catch(function() {
        record.get('errors').get('foo');
        // [{message: 'foo should be a number.', attribute: 'foo'}]
      });
      ```
       The `errors` property is useful for displaying error messages to
      the user.
       ```handlebars
      <label>Username: <Input @value={{@model.username}} /> </label>
      {{#each @model.errors.username as |error|}}
        <div class="error">
          {{error.message}}
        </div>
      {{/each}}
      <label>Email: <Input @value={{@model.email}} /> </label>
      {{#each @model.errors.email as |error|}}
        <div class="error">
          {{error.message}}
        </div>
      {{/each}}
      ```
        You can also access the special `messages` property on the error
      object to get an array of all the error strings.
       ```handlebars
      {{#each @model.errors.messages as |message|}}
        <div class="error">
          {{message}}
        </div>
      {{/each}}
      ```
       @property errors
      @public
      @type {Errors}
    */
    get errors() {
      let errors = Errors.create();
      errors._registerHandlers(() => {
        this._internalModel.send('becameInvalid');
      }, () => {
        this._internalModel.send('becameValid');
      });
      // TODO we should unify how errors gets populated
      // with the code managing the update. Probably a
      // lazy flush similar to retrieveLatest in ManyArray
      let recordData = Private.recordDataFor(this);
      let jsonApiErrors;
      if (recordData.getErrors) {
        jsonApiErrors = recordData.getErrors();
        if (jsonApiErrors) {
          let errorsHash = Private.errorsArrayToHash(jsonApiErrors);
          let errorKeys = Object.keys(errorsHash);
          for (let i = 0; i < errorKeys.length; i++) {
            errors._add(errorKeys[i], errorsHash[errorKeys[i]]);
          }
        }
      }
      return errors;
    }

    /**
      This property holds the `AdapterError` object with which
      last adapter operation was rejected.
       @property adapterError
      @public
      @type {AdapterError}
    */
    get adapterError() {
      return this.currentState.adapterError;
    }
    set adapterError(v) {
      throw new Error(`adapterError is not directly settable`);
    }

    /**
      Create a JSON representation of the record, using the serialization
      strategy of the store's adapter.
      `serialize` takes an optional hash as a parameter, currently
      supported options are:
      - `includeId`: `true` if the record's ID should be included in the
        JSON representation.
       @method serialize
      @public
      @param {Object} options
      @return {Object} an object whose values are primitive JSON values only
    */
    serialize(options) {
      return this._internalModel.createSnapshot().serialize(options);
    }

    /*
      We hook the default implementation to ensure
      our tagged properties are properly notified
      as well. We still super for everything because
      sync observers require a direct call occuring
      to trigger their flush. We wouldn't need to
      super in 4.0+ where sync observers are removed.
     */
    notifyPropertyChange(key) {
      let tag = peekTag(this, key);
      if (tag) {
        tag.notify();
      }
      super.notifyPropertyChange(key);
    }

    /**
      Marks the record as deleted but does not save it. You must call
      `save` afterwards if you want to persist it. You might use this
      method if you want to allow the user to still `rollbackAttributes()`
      after a delete was made.
       Example
       ```app/controllers/model/delete.js
      import Controller from '@ember/controller';
      import { action } from '@ember/object';
       export default class ModelDeleteController extends Controller {
        @action
        softDelete() {
          this.model.deleteRecord();
        }
         @action
        confirm() {
          this.model.save();
        }
         @action
        undo() {
          this.model.rollbackAttributes();
        }
      }
      ```
       @method deleteRecord
      @public
    */
    deleteRecord() {
      this.store.deleteRecord(this);
    }

    /**
      Same as `deleteRecord`, but saves the record immediately.
       Example
       ```app/controllers/model/delete.js
      import Controller from '@ember/controller';
      import { action } from '@ember/object';
       export default class ModelDeleteController extends Controller {
        @action
        delete() {
          this.model.destroyRecord().then(function() {
            this.transitionToRoute('model.index');
          });
        }
      }
      ```
       If you pass an object on the `adapterOptions` property of the options
      argument it will be passed to your adapter via the snapshot
       ```js
      record.destroyRecord({ adapterOptions: { subscribe: false } });
      ```
       ```app/adapters/post.js
      import MyCustomAdapter from './custom-adapter';
       export default class PostAdapter extends MyCustomAdapter {
        deleteRecord(store, type, snapshot) {
          if (snapshot.adapterOptions.subscribe) {
            // ...
          }
          // ...
        }
      }
      ```
       @method destroyRecord
      @public
      @param {Object} options
      @return {Promise} a promise that will be resolved when the adapter returns
      successfully or rejected if the adapter returns with an error.
    */
    destroyRecord(options) {
      this.deleteRecord();
      return this.save(options).then(_ => {
        runloop.run(() => {
          this.unloadRecord();
        });
        return this;
      });
    }

    /**
      Unloads the record from the store. This will not send a delete request
      to your server, it just unloads the record from memory.
       @method unloadRecord
      @public
    */
    unloadRecord() {
      if (this.isDestroyed) {
        return;
      }
      this.store.unloadRecord(this);
    }

    /**
      @method _notifyProperties
      @private
    */
    _notifyProperties(keys) {
      // changeProperties defers notifications until after the delegate
      // and protects with a try...finally block
      // previously used begin...endPropertyChanges but this is private API
      changeProperties(() => {
        let key;
        for (let i = 0, length = keys.length; i < length; i++) {
          key = keys[i];
          this.notifyPropertyChange(key);
        }
      });
    }

    /**
      Returns an object, whose keys are changed properties, and value is
      an [oldProp, newProp] array.
       The array represents the diff of the canonical state with the local state
      of the model. Note: if the model is created locally, the canonical state is
      empty since the adapter hasn't acknowledged the attributes yet:
       Example
       ```app/models/mascot.js
      import Model, { attr } from '@ember-data/model';
       export default class MascotModel extends Model {
        @attr('string') name;
        @attr('boolean', {
          defaultValue: false
        })
        isAdmin;
      }
      ```
       ```javascript
      let mascot = store.createRecord('mascot');
       mascot.changedAttributes(); // {}
       mascot.set('name', 'Tomster');
      mascot.changedAttributes(); // { name: [undefined, 'Tomster'] }
       mascot.set('isAdmin', true);
      mascot.changedAttributes(); // { isAdmin: [undefined, true], name: [undefined, 'Tomster'] }
       mascot.save().then(function() {
        mascot.changedAttributes(); // {}
         mascot.set('isAdmin', false);
        mascot.changedAttributes(); // { isAdmin: [true, false] }
      });
      ```
       @method changedAttributes
      @public
      @return {Object} an object, whose keys are changed properties,
        and value is an [oldProp, newProp] array.
    */
    changedAttributes() {
      return this._internalModel.changedAttributes();
    }

    /**
      If the model `hasDirtyAttributes` this function will discard any unsaved
      changes. If the model `isNew` it will be removed from the store.
       Example
       ```javascript
      record.get('name'); // 'Untitled Document'
      record.set('name', 'Doc 1');
      record.get('name'); // 'Doc 1'
      record.rollbackAttributes();
      record.get('name'); // 'Untitled Document'
      ```
       @since 1.13.0
      @method rollbackAttributes
      @public
    */
    rollbackAttributes() {
      this._internalModel.rollbackAttributes();
      this.currentState.cleanErrorRequests();
    }

    /**
      @method _createSnapshot
      @private
    */
    _createSnapshot() {
      return this._internalModel.createSnapshot();
    }
    toStringExtension() {
      // the _internalModel guard exists, because some dev-only deprecation code
      // (addListener via validatePropertyInjections) invokes toString before the
      // object is real.
      return this._internalModel && this._internalModel.id;
    }

    /**
      Save the record and persist any changes to the record to an
      external source via the adapter.
       Example
       ```javascript
      record.set('name', 'Tomster');
      record.save().then(function() {
        // Success callback
      }, function() {
        // Error callback
      });
      ```
      If you pass an object using the `adapterOptions` property of the options
     argument it will be passed to your adapter via the snapshot.
       ```js
      record.save({ adapterOptions: { subscribe: false } });
      ```
       ```app/adapters/post.js
      import MyCustomAdapter from './custom-adapter';
       export default class PostAdapter extends MyCustomAdapter {
        updateRecord(store, type, snapshot) {
          if (snapshot.adapterOptions.subscribe) {
            // ...
          }
          // ...
        }
      }
      ```
       @method save
      @public
      @param {Object} options
      @return {Promise} a promise that will be resolved when the adapter returns
      successfully or rejected if the adapter returns with an error.
    */
    save(options) {
      const promise = this._internalModel.save(options).then(() => this);
      {
        return Private.deprecatedPromiseObject(Private.PromiseObject.create({
          promise
        }));
      }
    }

    /**
      Reload the record from the adapter.
       This will only work if the record has already finished loading.
       Example
       ```app/controllers/model/view.js
      import Controller from '@ember/controller';
      import { action } from '@ember/object';
       export default class ViewController extends Controller {
        @action
        reload() {
          this.model.reload().then(function(model) {
          // do something with the reloaded model
          });
        }
      }
      ```
       @method reload
      @public
      @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter request
      @return {Promise} a promise that will be resolved with the record when the
      adapter returns successfully or rejected if the adapter returns
      with an error.
    */
    reload(options) {
      let wrappedAdapterOptions;
      if (typeof options === 'object' && options !== null && options.adapterOptions) {
        wrappedAdapterOptions = {
          adapterOptions: options.adapterOptions
        };
      }
      this.isReloading = true;
      return Private.PromiseObject.create({
        promise: this._internalModel.reload(wrappedAdapterOptions).then(() => this).finally(() => {
          this.isReloading = false;
        })
      });
    }
    attr() {
    }

    /**
      Get the reference for the specified belongsTo relationship.
       Example
       ```app/models/blog.js
      import Model, { belongsTo } from '@ember-data/model';
       export default class BlogModel extends Model {
        @belongsTo({ async: true }) user;
      }
      ```
       ```javascript
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
      let userRef = blog.belongsTo('user');
       // check if the user relationship is loaded
      let isLoaded = userRef.value() !== null;
       // get the record of the reference (null if not yet available)
      let user = userRef.value();
       // get the identifier of the reference
      if (userRef.remoteType() === "id") {
        let id = userRef.id();
      } else if (userRef.remoteType() === "link") {
        let link = userRef.link();
      }
       // load user (via store.findRecord or store.findBelongsTo)
      userRef.load().then(...)
       // or trigger a reload
      userRef.reload().then(...)
       // provide data for reference
      userRef.push({
        type: 'user',
        id: 1,
        attributes: {
          username: "@user"
        }
      }).then(function(user) {
        userRef.value() === user;
      });
      ```
       @method belongsTo
      @public
      @param {String} name of the relationship
      @since 2.5.0
      @return {BelongsToReference} reference for this relationship
    */
    belongsTo(name) {
      return this._internalModel.referenceFor('belongsTo', name);
    }

    /**
      Get the reference for the specified hasMany relationship.
       Example
       ```app/models/blog.js
      import Model, { hasMany } from '@ember-data/model';
       export default class BlogModel extends Model {
        @hasMany({ async: true }) comments;
      }
       let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            comments: {
              data: [
                { type: 'comment', id: 1 },
                { type: 'comment', id: 2 }
              ]
            }
          }
        }
      });
      let commentsRef = blog.hasMany('comments');
       // check if the comments are loaded already
      let isLoaded = commentsRef.value() !== null;
       // get the records of the reference (null if not yet available)
      let comments = commentsRef.value();
       // get the identifier of the reference
      if (commentsRef.remoteType() === "ids") {
        let ids = commentsRef.ids();
      } else if (commentsRef.remoteType() === "link") {
        let link = commentsRef.link();
      }
       // load comments (via store.findMany or store.findHasMany)
      commentsRef.load().then(...)
       // or trigger a reload
      commentsRef.reload().then(...)
       // provide data for reference
      commentsRef.push([{ type: 'comment', id: 1 }, { type: 'comment', id: 2 }]).then(function(comments) {
        commentsRef.value() === comments;
      });
      ```
       @method hasMany
      @public
      @param {String} name of the relationship
      @since 2.5.0
      @return {HasManyReference} reference for this relationship
    */
    hasMany(name) {
      return this._internalModel.referenceFor('hasMany', name);
    }

    /**
     Given a callback, iterates over each of the relationships in the model,
     invoking the callback with the name of each relationship and its relationship
     descriptor.
       The callback method you provide should have the following signature (all
     parameters are optional):
      ```javascript
     function(name, descriptor);
     ```
      - `name` the name of the current property in the iteration
     - `descriptor` the meta object that describes this relationship
      The relationship descriptor argument is an object with the following properties.
      - **key** <span class="type">String</span> the name of this relationship on the Model
     - **kind** <span class="type">String</span> "hasMany" or "belongsTo"
     - **options** <span class="type">Object</span> the original options hash passed when the relationship was declared
     - **parentType** <span class="type">Model</span> the type of the Model that owns this relationship
     - **type** <span class="type">String</span> the type name of the related Model
      Note that in addition to a callback, you can also pass an optional target
     object that will be set as `this` on the context.
      Example
      ```app/serializers/application.js
     import JSONSerializer from '@ember-data/serializer/json';
      export default class ApplicationSerializer extends JSONSerializer {
        serialize(record, options) {
        let json = {};
         record.eachRelationship(function(name, descriptor) {
          if (descriptor.kind === 'hasMany') {
            let serializedHasManyName = name.toUpperCase() + '_IDS';
            json[serializedHasManyName] = record.get(name).mapBy('id');
          }
        });
         return json;
      }
    }
     ```
      @method eachRelationship
      @public
     @param {Function} callback the callback to invoke
     @param {any} binding the value to which the callback's `this` should be bound
     */
    eachRelationship(callback, binding) {
      this.constructor.eachRelationship(callback, binding);
    }
    relationshipFor(name) {
      return this.constructor.relationshipsByName.get(name);
    }
    inverseFor(key) {
      return this.constructor.inverseFor(key, this._internalModel.store);
    }
    eachAttribute(callback, binding) {
      this.constructor.eachAttribute(callback, binding);
    }
    /*
     These class methods below provide relationship
     introspection abilities about relationships.
      A note about the computed properties contained here:
      **These properties are effectively sealed once called for the first time.**
     To avoid repeatedly doing expensive iteration over a model's fields, these
     values are computed once and then cached for the remainder of the runtime of
     your application.
      If your application needs to modify a class after its initial definition
     (for example, using `reopen()` to add additional attributes), make sure you
     do it before using your model with the store, which uses these properties
     extensively.
     */
    /**
     For a given relationship name, returns the model type of the relationship.
      For example, if you define a model like this:
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany('comment') comments;
     }
     ```
      Calling `store.modelFor('post').typeForRelationship('comments', store)` will return `Comment`.
      @method typeForRelationship
      @public
     @static
     @param {String} name the name of the relationship
     @param {store} store an instance of Store
     @return {Model} the type of the relationship, or undefined
     */
    static typeForRelationship(name, store) {
      let relationship = this.relationshipsByName.get(name);
      return relationship && store.modelFor(relationship.type);
    }
    static get inverseMap() {
      return Object.create(null);
    }

    /**
     Find the relationship which is the inverse of the one asked for.
      For example, if you define models like this:
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
        @hasMany('message') comments;
      }
     ```
      ```app/models/message.js
     import Model from '@ember-data/model';
     import { belongsTo } from '@ember-decorators/data';
      export default class MessageModel extends Model {
        @belongsTo('post') owner;
      }
     ```
      ``` js
     store.modelFor('post').inverseFor('comments', store) // { type: App.Message, name: 'owner', kind: 'belongsTo' }
     store.modelFor('message').inverseFor('owner', store) // { type: App.Post, name: 'comments', kind: 'hasMany' }
     ```
      @method inverseFor
      @public
     @static
     @param {String} name the name of the relationship
     @param {Store} store
     @return {Object} the inverse relationship, or null
     */
    static inverseFor(name, store) {
      let inverseMap = this.inverseMap;
      if (inverseMap[name]) {
        return inverseMap[name];
      } else {
        let inverse = this._findInverseFor(name, store);
        inverseMap[name] = inverse;
        return inverse;
      }
    }

    //Calculate the inverse, ignoring the cache
    static _findInverseFor(name, store) {
      let inverseType = this.typeForRelationship(name, store);
      if (!inverseType) {
        return null;
      }
      let propertyMeta = this.metaForProperty(name);
      //If inverse is manually specified to be null, like  `comments: hasMany('message', { inverse: null })`
      let options = propertyMeta.options;
      if (options.inverse === null) {
        return null;
      }
      let inverseName, inverseKind, inverse, inverseOptions;

      //If inverse is specified manually, return the inverse
      if (options.inverse) {
        inverseName = options.inverse;
        inverse = inverseType.relationshipsByName.get(inverseName);
        inverseKind = inverse.kind;
        inverseOptions = inverse.options;
      } else {
        //No inverse was specified manually, we need to use a heuristic to guess one
        if (propertyMeta.type === propertyMeta.parentModelName) ;
        let possibleRelationships = findPossibleInverses(this, inverseType, name);
        if (possibleRelationships.length === 0) {
          return null;
        }
        let filteredRelationships = possibleRelationships.filter(possibleRelationship => {
          let optionsForRelationship = inverseType.metaForProperty(possibleRelationship.name).options;
          return name === optionsForRelationship.inverse;
        });
        if (filteredRelationships.length === 1) {
          possibleRelationships = filteredRelationships;
        }
        inverseName = possibleRelationships[0].name;
        inverseKind = possibleRelationships[0].kind;
        inverseOptions = possibleRelationships[0].options;
      }
      return {
        type: inverseType,
        name: inverseName,
        kind: inverseKind,
        options: inverseOptions
      };
    }

    /**
     The model's relationships as a map, keyed on the type of the
     relationship. The value of each entry is an array containing a descriptor
     for each relationship with that type, describing the name of the relationship
     as well as the type.
      For example, given the following model definition:
      ```app/models/blog.js
     import Model, { belongsTo, hasMany } from '@ember-data/model';
      export default class BlogModel extends Model {
        @hasMany('user') users;
        @belongsTo('user') owner;
        @hasMany('post') posts;
      }
     ```
      This computed property would return a map describing these
     relationships, like this:
      ```javascript
     import { get } from '@ember/object';
     import Blog from 'app/models/blog';
     import User from 'app/models/user';
     import Post from 'app/models/post';
      let relationships = Blog.relationships;
     relationships.get('user');
     //=> [ { name: 'users', kind: 'hasMany' },
     //     { name: 'owner', kind: 'belongsTo' } ]
     relationships.get('post');
     //=> [ { name: 'posts', kind: 'hasMany' } ]
     ```
      @property relationships
      @public
     @static
     @type Map
     @readOnly
     */

    static get relationships() {
      let map = new Map();
      let relationshipsByName = this.relationshipsByName;

      // Loop through each computed property on the class
      relationshipsByName.forEach(desc => {
        let {
          type
        } = desc;
        if (!map.has(type)) {
          map.set(type, []);
        }
        map.get(type).push(desc);
      });
      return map;
    }

    /**
     A hash containing lists of the model's relationships, grouped
     by the relationship kind. For example, given a model with this
     definition:
      ```app/models/blog.js
     import Model, { belongsTo, hasMany } from '@ember-data/model';
      export default class BlogModel extends Model {
        @hasMany('user') users;
        @belongsTo('user') owner;
         @hasMany('post') posts;
      }
     ```
      This property would contain the following:
      ```javascript
     import { get } from '@ember/object';
     import Blog from 'app/models/blog';
      let relationshipNames = Blog.relationshipNames;
     relationshipNames.hasMany;
     //=> ['users', 'posts']
     relationshipNames.belongsTo;
     //=> ['owner']
     ```
      @property relationshipNames
      @public
     @static
     @type Object
     @readOnly
     */
    static get relationshipNames() {
      let names = {
        hasMany: [],
        belongsTo: []
      };
      this.eachComputedProperty((name, meta) => {
        if (meta.isRelationship) {
          names[meta.kind].push(name);
        }
      });
      return names;
    }

    /**
     An array of types directly related to a model. Each type will be
     included once, regardless of the number of relationships it has with
     the model.
      For example, given a model with this definition:
      ```app/models/blog.js
     import Model, { belongsTo, hasMany } from '@ember-data/model';
      export default class BlogModel extends Model {
        @hasMany('user') users;
        @belongsTo('user') owner;
         @hasMany('post') posts;
      }
     ```
      This property would contain the following:
      ```javascript
     import { get } from '@ember/object';
     import Blog from 'app/models/blog';
      let relatedTypes = Blog.relatedTypes');
     //=> [ User, Post ]
     ```
      @property relatedTypes
      @public
     @static
     @type Ember.Array
     @readOnly
     */
    static get relatedTypes() {
      let types = [];
      let rels = this.relationshipsObject;
      let relationships = Object.keys(rels);

      // create an array of the unique types involved
      // in relationships
      for (let i = 0; i < relationships.length; i++) {
        let name = relationships[i];
        let meta = rels[name];
        let modelName = meta.type;
        if (types.indexOf(modelName) === -1) {
          types.push(modelName);
        }
      }
      return types;
    }

    /**
     A map whose keys are the relationships of a model and whose values are
     relationship descriptors.
      For example, given a model with this
     definition:
      ```app/models/blog.js
     import Model, { belongsTo, hasMany } from '@ember-data/model';
      export default class BlogModel extends Model {
        @hasMany('user') users;
        @belongsTo('user') owner;
         @hasMany('post') posts;
      }
     ```
      This property would contain the following:
      ```javascript
     import { get } from '@ember/object';
     import Blog from 'app/models/blog';
      let relationshipsByName = Blog.relationshipsByName;
     relationshipsByName.get('users');
     //=> { key: 'users', kind: 'hasMany', type: 'user', options: Object, isRelationship: true }
     relationshipsByName.get('owner');
     //=> { key: 'owner', kind: 'belongsTo', type: 'user', options: Object, isRelationship: true }
     ```
      @property relationshipsByName
      @public
     @static
     @type Map
     @readOnly
     */
    static get relationshipsByName() {
      let map = new Map();
      let rels = this.relationshipsObject;
      let relationships = Object.keys(rels);
      for (let i = 0; i < relationships.length; i++) {
        let key = relationships[i];
        let value = rels[key];
        map.set(value.key, value);
      }
      return map;
    }
    static get relationshipsObject() {
      let relationships = Object.create(null);
      let modelName = this.modelName;
      this.eachComputedProperty((name, meta) => {
        if (meta.isRelationship) {
          meta.key = name;
          meta.name = name;
          meta.parentModelName = modelName;
          relationships[name] = relationshipFromMeta(meta);
        }
      });
      return relationships;
    }

    /**
     A map whose keys are the fields of the model and whose values are strings
     describing the kind of the field. A model's fields are the union of all of its
     attributes and relationships.
      For example:
      ```app/models/blog.js
     import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
      export default class BlogModel extends Model {
        @hasMany('user') users;
        @belongsTo('user') owner;
         @hasMany('post') posts;
         @attr('string') title;
      }
     ```
      ```js
     import { get } from '@ember/object';
     import Blog from 'app/models/blog'
      let fields = Blog.fields;
     fields.forEach(function(kind, field) {
        console.log(field, kind);
      });
      // prints:
     // users, hasMany
     // owner, belongsTo
     // posts, hasMany
     // title, attribute
     ```
      @property fields
      @public
     @static
     @type Map
     @readOnly
     */
    static get fields() {
      let map = new Map();
      this.eachComputedProperty((name, meta) => {
        if (meta.isRelationship) {
          map.set(name, meta.kind);
        } else if (meta.isAttribute) {
          map.set(name, 'attribute');
        }
      });
      return map;
    }

    /**
     Given a callback, iterates over each of the relationships in the model,
     invoking the callback with the name of each relationship and its relationship
     descriptor.
      @method eachRelationship
      @public
     @static
     @param {Function} callback the callback to invoke
     @param {any} binding the value to which the callback's `this` should be bound
     */
    static eachRelationship(callback, binding) {
      this.relationshipsByName.forEach((relationship, name) => {
        callback.call(binding, name, relationship);
      });
    }

    /**
     Given a callback, iterates over each of the types related to a model,
     invoking the callback with the related type's class. Each type will be
     returned just once, regardless of how many different relationships it has
     with a model.
      @method eachRelatedType
      @public
     @static
     @param {Function} callback the callback to invoke
     @param {any} binding the value to which the callback's `this` should be bound
     */
    static eachRelatedType(callback, binding) {
      let relationshipTypes = this.relatedTypes;
      for (let i = 0; i < relationshipTypes.length; i++) {
        let type = relationshipTypes[i];
        callback.call(binding, type);
      }
    }
    static determineRelationshipType(knownSide, store) {
      let knownKey = knownSide.key;
      let knownKind = knownSide.kind;
      let inverse = this.inverseFor(knownKey, store);
      // let key;
      let otherKind;
      if (!inverse) {
        return knownKind === 'belongsTo' ? 'oneToNone' : 'manyToNone';
      }

      // key = inverse.name;
      otherKind = inverse.kind;
      if (otherKind === 'belongsTo') {
        return knownKind === 'belongsTo' ? 'oneToOne' : 'manyToOne';
      } else {
        return knownKind === 'belongsTo' ? 'oneToMany' : 'manyToMany';
      }
    }

    /**
     A map whose keys are the attributes of the model (properties
     described by attr) and whose values are the meta object for the
     property.
      Example
      ```app/models/person.js
     import Model, { attr } from '@ember-data/model';
      export default class PersonModel extends Model {
        @attr('string') firstName;
        @attr('string') lastName;
        @attr('date') birthday;
      }
     ```
      ```javascript
     import { get } from '@ember/object';
     import Person from 'app/models/person'
      let attributes = Person.attributes
      attributes.forEach(function(meta, name) {
        console.log(name, meta);
      });
      // prints:
     // firstName {type: "string", isAttribute: true, options: Object, parentType: function, name: "firstName"}
     // lastName {type: "string", isAttribute: true, options: Object, parentType: function, name: "lastName"}
     // birthday {type: "date", isAttribute: true, options: Object, parentType: function, name: "birthday"}
     ```
      @property attributes
      @public
     @static
     @type {Map}
     @readOnly
     */
    static get attributes() {
      let map = new Map();
      this.eachComputedProperty((name, meta) => {
        if (meta.isAttribute) {
          meta.name = name;
          map.set(name, meta);
        }
      });
      return map;
    }

    /**
     A map whose keys are the attributes of the model (properties
     described by attr) and whose values are type of transformation
     applied to each attribute. This map does not include any
     attributes that do not have an transformation type.
      Example
      ```app/models/person.js
     import Model, { attr } from '@ember-data/model';
      export default class PersonModel extends Model {
        @attr firstName;
        @attr('string') lastName;
        @attr('date') birthday;
      }
     ```
      ```javascript
     import { get } from '@ember/object';
     import Person from 'app/models/person';
      let transformedAttributes = Person.transformedAttributes
      transformedAttributes.forEach(function(field, type) {
        console.log(field, type);
      });
      // prints:
     // lastName string
     // birthday date
     ```
      @property transformedAttributes
      @public
     @static
     @type {Map}
     @readOnly
     */
    static get transformedAttributes() {
      let map = new Map();
      this.eachAttribute((key, meta) => {
        if (meta.type) {
          map.set(key, meta.type);
        }
      });
      return map;
    }

    /**
     Iterates through the attributes of the model, calling the passed function on each
     attribute.
      The callback method you provide should have the following signature (all
     parameters are optional):
      ```javascript
     function(name, meta);
     ```
      - `name` the name of the current property in the iteration
     - `meta` the meta object for the attribute property in the iteration
      Note that in addition to a callback, you can also pass an optional target
     object that will be set as `this` on the context.
      Example
      ```javascript
     import Model, { attr } from '@ember-data/model';
      class PersonModel extends Model {
        @attr('string') firstName;
        @attr('string') lastName;
        @attr('date') birthday;
      }
      PersonModel.eachAttribute(function(name, meta) {
        console.log(name, meta);
      });
      // prints:
     // firstName {type: "string", isAttribute: true, options: Object, parentType: function, name: "firstName"}
     // lastName {type: "string", isAttribute: true, options: Object, parentType: function, name: "lastName"}
     // birthday {type: "date", isAttribute: true, options: Object, parentType: function, name: "birthday"}
     ```
      @method eachAttribute
      @public
     @param {Function} callback The callback to execute
     @param {Object} [binding] the value to which the callback's `this` should be bound
     @static
     */
    static eachAttribute(callback, binding) {
      this.attributes.forEach((meta, name) => {
        callback.call(binding, name, meta);
      });
    }

    /**
     Iterates through the transformedAttributes of the model, calling
     the passed function on each attribute. Note the callback will not be
     called for any attributes that do not have an transformation type.
      The callback method you provide should have the following signature (all
     parameters are optional):
      ```javascript
     function(name, type);
     ```
      - `name` the name of the current property in the iteration
     - `type` a string containing the name of the type of transformed
     applied to the attribute
      Note that in addition to a callback, you can also pass an optional target
     object that will be set as `this` on the context.
      Example
      ```javascript
     import Model, { attr } from '@ember-data/model';
      let Person = Model.extend({
        firstName: attr(),
        lastName: attr('string'),
        birthday: attr('date')
      });
      Person.eachTransformedAttribute(function(name, type) {
        console.log(name, type);
      });
      // prints:
     // lastName string
     // birthday date
     ```
      @method eachTransformedAttribute
      @public
     @param {Function} callback The callback to execute
     @param {Object} [binding] the value to which the callback's `this` should be bound
     @static
     */
    static eachTransformedAttribute(callback, binding) {
      this.transformedAttributes.forEach((type, name) => {
        callback.call(binding, name, type);
      });
    }

    /**
     Returns the name of the model class.
      @method toString
      @public
     @static
     */
    static toString() {
      return `model:${EmberObject.get(this, 'modelName')}`;
    }
  }, _class2.isModel = true, _class2.modelName = null, _class2), (_applyDecoratedDescriptor$1(_class$1.prototype, "isEmpty", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isEmpty"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isLoading", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isLoading"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isLoaded", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isLoaded"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "hasDirtyAttributes", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "hasDirtyAttributes"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isSaving", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isSaving"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isDeleted", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isDeleted"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isNew", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isNew"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isValid", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isValid"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "dirtyType", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "dirtyType"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "isError", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "isError"), _class$1.prototype), _descriptor$1 = _applyDecoratedDescriptor$1(_class$1.prototype, "isReloading", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _applyDecoratedDescriptor$1(_class$1.prototype, "id", [tagged], Object.getOwnPropertyDescriptor(_class$1.prototype, "id"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "currentState", [tagged], Object.getOwnPropertyDescriptor(_class$1.prototype, "currentState"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "errors", [computeOnce], Object.getOwnPropertyDescriptor(_class$1.prototype, "errors"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, "adapterError", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "adapterError"), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1, "inverseMap", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "inverseMap"), _class$1), _applyDecoratedDescriptor$1(_class$1, "relationships", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "relationships"), _class$1), _applyDecoratedDescriptor$1(_class$1, "relationshipNames", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "relationshipNames"), _class$1), _applyDecoratedDescriptor$1(_class$1, "relatedTypes", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "relatedTypes"), _class$1), _applyDecoratedDescriptor$1(_class$1, "relationshipsByName", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "relationshipsByName"), _class$1), _applyDecoratedDescriptor$1(_class$1, "relationshipsObject", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "relationshipsObject"), _class$1), _applyDecoratedDescriptor$1(_class$1, "fields", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "fields"), _class$1), _applyDecoratedDescriptor$1(_class$1, "attributes", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "attributes"), _class$1), _applyDecoratedDescriptor$1(_class$1, "transformedAttributes", [computeOnce], Object.getOwnPropertyDescriptor(_class$1, "transformedAttributes"), _class$1)), _class$1); // this is required to prevent `init` from passing
  // the values initialized during create to `setUnknownProperty`
  Model.prototype._internalModel = null;
  Model.prototype.store = null;
  Model.prototype._createProps = null;
  {
    /**
     Provides info about the model for debugging purposes
     by grouping the properties into more semantic groups.
      Meant to be used by debugging tools such as the Chrome Ember Extension.
      - Groups all attributes in "Attributes" group.
     - Groups all belongsTo relationships in "Belongs To" group.
     - Groups all hasMany relationships in "Has Many" group.
     - Groups all flags in "Flags" group.
     - Flags relationship CPs as expensive properties.
      @method _debugInfo
     @for Model
     @private
     */
    Model.prototype._debugInfo = function () {
      let attributes = ['id'];
      let relationships = {};
      let expensiveProperties = [];
      this.eachAttribute((name, meta) => attributes.push(name));
      let groups = [{
        name: 'Attributes',
        properties: attributes,
        expand: true
      }];
      this.eachRelationship((name, relationship) => {
        let properties = relationships[relationship.kind];
        if (properties === undefined) {
          properties = relationships[relationship.kind] = [];
          groups.push({
            name: relationship.kind,
            properties,
            expand: true
          });
        }
        properties.push(name);
        expensiveProperties.push(name);
      });
      groups.push({
        name: 'Flags',
        properties: ['isLoaded', 'hasDirtyAttributes', 'isSaving', 'isDeleted', 'isError', 'isNew', 'isValid']
      });
      return {
        propertyInfo: {
          // include all other mixins / properties (not just the grouped ones)
          includeOtherProperties: true,
          groups: groups,
          // don't pre-calculate unless cached
          expensiveProperties: expensiveProperties
        }
      };
    };
  }

  /**
    @module @ember-data/model
  */

  /**
    @method diffArray
    @internal
    @param {Array} oldArray the old array
    @param {Array} newArray the new array
    @return {hash} {
        firstChangeIndex: <integer>,  // null if no change
        addedCount: <integer>,        // 0 if no change
        removedCount: <integer>       // 0 if no change
      }
  */
  function diffArray(oldArray, newArray) {
    const oldLength = oldArray.length;
    const newLength = newArray.length;
    const shortestLength = Math.min(oldLength, newLength);
    let firstChangeIndex = null; // null signifies no changes

    // find the first change
    for (let i = 0; i < shortestLength; i++) {
      // compare each item in the array
      if (oldArray[i] !== newArray[i]) {
        firstChangeIndex = i;
        break;
      }
    }
    if (firstChangeIndex === null && newLength !== oldLength) {
      // no change found in the overlapping block
      // and array lengths differ,
      // so change starts at end of overlap
      firstChangeIndex = shortestLength;
    }
    let addedCount = 0;
    let removedCount = 0;
    if (firstChangeIndex !== null) {
      // we found a change, find the end of the change
      let unchangedEndBlockLength = shortestLength - firstChangeIndex;
      // walk back from the end of both arrays until we find a change
      for (let i = 1; i <= shortestLength; i++) {
        // compare each item in the array
        if (oldArray[oldLength - i] !== newArray[newLength - i]) {
          unchangedEndBlockLength = i - 1;
          break;
        }
      }
      addedCount = newLength - unchangedEndBlockLength - firstChangeIndex;
      removedCount = oldLength - unchangedEndBlockLength - firstChangeIndex;
    }
    return {
      firstChangeIndex,
      addedCount,
      removedCount
    };
  }

  /**
    A `ManyArray` is a `MutableArray` that represents the contents of a has-many
    relationship.

    The `ManyArray` is instantiated lazily the first time the relationship is
    requested.

    This class is not intended to be directly instantiated by consuming applications.

    ### Inverses

    Often, the relationships in Ember Data applications will have
    an inverse. For example, imagine the following models are
    defined:

    ```app/models/post.js
    import Model, { hasMany } from '@ember-data/model';

    export default class PostModel extends Model {
      @hasMany('comment') comments;
    }
    ```

    ```app/models/comment.js
    import Model, { belongsTo } from '@ember-data/model';

    export default class CommentModel extends Model {
      @belongsTo('post') post;
    }
    ```

    If you created a new instance of `Post` and added
    a `Comment` record to its `comments` has-many
    relationship, you would expect the comment's `post`
    property to be set to the post that contained
    the has-many.

    We call the record to which a relationship belongs-to the
    relationship's _owner_.

    @class ManyArray
    @public
    @extends Ember.EmberObject
    @uses Ember.MutableArray
  */
  var manyArray = EmberObject__default["default"].extend(MutableArray__default["default"], {
    isAsync: false,
    isLoaded: false,
    init() {
      this._super(...arguments);

      /**
      The loading state of this array
       @property {Boolean} isLoaded
      @public
      */
      this.isLoaded = this.isLoaded || false;
      this._length = 0;

      /**
      Metadata associated with the request for async hasMany relationships.
       Example
       Given that the server returns the following JSON payload when fetching a
      hasMany relationship:
       ```js
      {
        "comments": [{
          "id": 1,
          "comment": "This is the first comment",
        }, {
      // ...
        }],
         "meta": {
          "page": 1,
          "total": 5
        }
      }
      ```
       You can then access the meta data via the `meta` property:
       ```js
      let comments = await post.comments;
      let meta = comments.meta;
       // meta.page => 1
      // meta.total => 5
      ```
       @property {Object | null} meta
      @public
      */
      this._meta = this._meta || null;

      /**
       * Retrieve the links for this relationship
       *
       @property {Object | null} links
       @public
       */
      this._links = this._links || null;

      /**
      `true` if the relationship is polymorphic, `false` otherwise.
       @property {Boolean} isPolymorphic
      @private
      */
      this.isPolymorphic = this.isPolymorphic || false;

      /**
      The relationship which manages this array.
       @property {ManyRelationship} relationship
      @private
      */
      this.currentState = [];
      this._isUpdating = false;
      this._isDirty = false;
      /*
       * Unfortunately ArrayProxy adds it's observers lazily,
       * so in a first-render situation we may sometimes notify
       * prior to the ArrayProxy having installed it's observers
       * (which occurs during _revalidate()).
       *
       * This leads to the flush occuring on access, the flush takes
       * the hasObservers codepath which in code out of our control
       * notifies again leading to a glimmer rendering invalidation error.
       *
       * We use this flag to detect the case where we notified without
       * array observers but observers were installed prior to flush.
       *
       * We do not need to fire array observers at all in this case
       * since it will be the first-access for those observers.
       */
      this._hasNotified = false;
      // make sure we initialize to the correct state
      // since the user has already accessed
      this.retrieveLatest();
    },
    // TODO refactor away _hasArrayObservers for tests
    get _hasArrayObservers() {
      return this.hasArrayObservers || this.__hasArrayObservers;
    },
    notify() {
      this._isDirty = true;
      if (this._hasArrayObservers && !this._hasNotified) {
        this.retrieveLatest();
      } else {
        this._hasNotified = true;
        this.notifyPropertyChange('[]');
        this.notifyPropertyChange('length');
        this.notifyPropertyChange('firstObject');
        this.notifyPropertyChange('lastObject');
      }
    },
    get length() {
      if (this._isDirty) {
        this.retrieveLatest();
      }
      // By using `get()`, the tracking system knows to pay attention to changes that occur.
      EmberObject.get(this, '[]');
      return this._length;
    },
    set length(value) {
      this._length = value;
    },
    get links() {
      EmberObject.get(this, '[]');
      if (this._isDirty) {
        this.retrieveLatest();
      }
      return this._links;
    },
    set links(v) {
      this._links = v;
    },
    get meta() {
      EmberObject.get(this, '[]');
      if (this._isDirty) {
        this.retrieveLatest();
      }
      return this._meta;
    },
    set meta(v) {
      this._meta = v;
    },
    objectAt(index) {
      if (this._isDirty) {
        this.retrieveLatest();
      }
      let internalModel = this.currentState[index];
      if (internalModel === undefined) {
        return;
      }
      return internalModel.getRecord();
    },
    replace(idx, amt, objects) {
      this.store._backburner.join(() => {
        let internalModels;
        if (amt > 0) {
          internalModels = this.currentState.slice(idx, idx + amt);
          this.recordData.removeFromHasMany(this.key, internalModels.map(im => Private.recordDataFor(im)));
        }
        if (objects) {
          this.recordData.addToHasMany(this.key, objects.map(obj => Private.recordDataFor(obj)), idx);
        }
        this.notify();
      });
    },
    retrieveLatest() {
      // It’s possible the parent side of the relationship may have been destroyed by this point
      if (this.isDestroyed || this.isDestroying || this._isUpdating) {
        return;
      }
      this._isDirty = false;
      this._isUpdating = true;
      let jsonApi = this.recordData.getHasMany(this.key);
      let internalModels = [];
      if (jsonApi.data) {
        for (let i = 0; i < jsonApi.data.length; i++) {
          let im = this.store._internalModelForResource(jsonApi.data[i]);
          let shouldRemove = im._isDematerializing || im.currentState.isEmpty || !im.currentState.isLoaded;
          if (!shouldRemove) {
            internalModels.push(im);
          }
        }
      }
      if (jsonApi.meta) {
        this._meta = jsonApi.meta;
      }
      if (jsonApi.links) {
        this._links = jsonApi.links;
      }
      if (this._hasArrayObservers && !this._hasNotified) {
        // diff to find changes
        let diff = diffArray(this.currentState, internalModels);
        // it's null if no change found
        if (diff.firstChangeIndex !== null) {
          // we found a change
          this.arrayContentWillChange(diff.firstChangeIndex, diff.removedCount, diff.addedCount);
          this._length = internalModels.length;
          this.currentState = internalModels;
          this.arrayContentDidChange(diff.firstChangeIndex, diff.removedCount, diff.addedCount);
        }
      } else {
        this._hasNotified = false;
        this._length = internalModels.length;
        this.currentState = internalModels;
      }
      this._isUpdating = false;
    },
    /**
      Reloads all of the records in the manyArray. If the manyArray
      holds a relationship that was originally fetched using a links url
      Ember Data will revisit the original links url to repopulate the
      relationship.
       If the manyArray holds the result of a `store.query()` reload will
      re-run the original query.
       Example
       ```javascript
      let user = store.peekRecord('user', '1')
      await login(user);
       let permissions = await user.permissions;
      await permissions.reload();
      ```
       @method reload
      @public
    */
    reload(options) {
      // TODO this is odd, we don't ask the store for anything else like this?
      return this.store.reloadManyArray(this, this.internalModel, this.key, options);
    },
    /**
      Saves all of the records in the `ManyArray`.
       Example
       ```javascript
      let inbox = await store.findRecord('inbox', '1');
      let messages = await inbox.messages;
      messages.forEach((message) => {
        message.isRead = true;
      });
      messages.save();
      ```
       @method save
      @public
      @return {PromiseArray} promise
    */
    save() {
      let manyArray = this;
      let promiseLabel = 'DS: ManyArray#save ' + this.type;
      let promise = rsvp.all(this.invoke('save'), promiseLabel).then(() => manyArray, null, 'DS: ManyArray#save return ManyArray');

      // TODO deprecate returning a promiseArray here
      return Private.PromiseArray.create({
        promise
      });
    },
    /**
      Create a child record within the owner
       @method createRecord
      @public
      @param {Object} hash
      @return {Model} record
    */
    createRecord(hash) {
      const {
        store,
        type
      } = this;
      let record = store.createRecord(type.modelName, hash);
      this.pushObject(record);
      return record;
    }
  });

  /**
   @module @ember-data/model
   */

  /**
    A PromiseBelongsTo is a PromiseObject that also proxies certain method calls
    to the underlying belongsTo model.
    Right now we proxy:

      * `reload()`

    @class PromiseBelongsTo
    @extends PromiseObject
    @private
  */
  const PromiseBelongsTo = Private.PromiseObject.extend({
    // we don't proxy meta because we would need to proxy it to the relationship state container
    //  however, meta on relationships does not trigger change notifications.
    //  if you need relationship meta, you should do `record.belongsTo(relationshipName).meta()`
    meta: EmberObject.computed(function () {
    }),
    reload(options) {
      let {
        key,
        store,
        originatingInternalModel
      } = this._belongsToState;
      return store.reloadBelongsTo(this, originatingInternalModel, key, options).then(() => this);
    }
  });

  var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
  function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }

  /**
   @module @ember-data/model
   */
  /**
    This class is returned as the result of accessing an async hasMany relationship
    on an instance of a Model extending from `@ember-data/model`.

    A PromiseManyArray is an array-like proxy that also proxies certain method calls
    to the underlying ManyArray in addition to being "promisified".

    Right now we proxy:

      * `reload()`
      * `createRecord()`

    This promise-proxy behavior is primarily to ensure that async relationship interact
    nicely with templates. In your JS code you should resolve the promise first.

    ```js
    const comments = await post.comments;
    ```

    @class PromiseManyArray
    @public
  */
  let PromiseManyArray = (_class = class PromiseManyArray {
    constructor(promise, content) {
      //---- Methods/Properties on ArrayProxy that we will keep as our API
      _initializerDefineProperty(this, "content", _descriptor, this);
      //----  Properties/Methods from the PromiseProxyMixin that we will keep as our API
      /**
       * Whether the loading promise is still pending
       *
       * @property {boolean} isPending
       * @public
       */
      _initializerDefineProperty(this, "isPending", _descriptor2, this);
      /**
       * Whether the loading promise rejected
       *
       * @property {boolean} isRejected
       * @public
       */
      _initializerDefineProperty(this, "isRejected", _descriptor3, this);
      /**
       * Whether the loading promise succeeded
       *
       * @property {boolean} isFulfilled
       * @public
       */
      _initializerDefineProperty(this, "isFulfilled", _descriptor4, this);
      /**
       * Whether the loading promise completed (resolved or rejected)
       *
       * @property {boolean} isSettled
       * @public
       */
      _initializerDefineProperty(this, "isSettled", _descriptor5, this);
      this._update(promise, content);
      this.isDestroyed = false;
      this.isDestroying = false;
      const meta = Ember__default["default"].meta(this);
      meta.hasMixin = mixin => {
        if (mixin === EmberArray__default["default"]) {
          return true;
        }
        return false;
      };
    }
    /**
     * Retrieve the length of the content
     * @property length
     * @public
     */
    get length() {
      // shouldn't be needed, but ends up being needed
      // for computed chains even in 4.x
      this['[]'];
      return this.content ? this.content.length : 0;
    }

    // ember-source < 3.23 (e.g. 3.20 lts)
    // requires that the tag `'[]'` be notified
    // on the ArrayProxy in order for `{{#each}}`
    // to recompute. We entangle the '[]' tag from
    get '[]'() {
      return this.content ? this.content['[]'] : this.content;
    }

    /**
     * Iterate the proxied content. Called by the glimmer iterator in #each
     *
     * @method forEach
     * @param cb
     * @returns
     * @private
     */
    forEach(cb) {
      this['[]']; // needed for < 3.23 support e.g. 3.20 lts
      if (this.content && this.length) {
        this.content.forEach(cb);
      }
    }
    /**
     * chain this promise
     *
     * @method then
     * @public
     * @param success
     * @param fail
     * @returns Promise
     */
    then(s, f) {
      return this.promise.then(s, f);
    }

    /**
     * catch errors thrown by this promise
     * @method catch
     * @public
     * @param callback
     * @returns Promise
     */
    catch(cb) {
      return this.promise.catch(cb);
    }

    /**
     * run cleanup after this promise completes
     *
     * @method finally
     * @public
     * @param callback
     * @returns Promise
     */
    finally(cb) {
      return this.promise.finally(cb);
    }

    //---- Methods on EmberObject that we should keep

    destroy() {
      this.isDestroying = true;
      this.isDestroyed = true;
      this.content = null;
      this.promise = null;
    }

    //---- Methods/Properties on ManyArray that we own and proxy to

    /**
     * Retrieve the links for this relationship
     * @property links
     * @public
     */
    get links() {
      return this.content ? this.content.links : undefined;
    }

    /**
     * Retrieve the meta for this relationship
     * @property meta
     * @public
     */
    get meta() {
      return this.content ? this.content.meta : undefined;
    }

    /**
     * Reload the relationship
     * @method reload
     * @public
     * @param options
     * @returns
     */
    reload(options) {
      this.content.reload(options);
      return this;
    }

    //---- Our own stuff

    _update(promise, content) {
      if (content !== undefined) {
        this.content = content;
      }
      this.promise = tapPromise(this, promise);
    }
    static create(_ref) {
      let {
        promise,
        content
      } = _ref;
      return new this(promise, content);
    }

    // Methods on ManyArray which people should resolve the relationship first before calling
    createRecord() {
      return this.content.createRecord(...arguments);
    }

    // Properties/Methods on ArrayProxy we should deprecate

    get firstObject() {
      return this.content ? this.content.firstObject : undefined;
    }
    get lastObject() {
      return this.content ? this.content.lastObject : undefined;
    }
  }, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "content", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return null;
    }
  }), _applyDecoratedDescriptor(_class.prototype, "length", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "length"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, '[]', [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, '[]'), _class.prototype), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "isPending", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isRejected", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "isFulfilled", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isSettled", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return false;
    }
  }), _applyDecoratedDescriptor(_class.prototype, "links", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "links"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "meta", [compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "meta"), _class.prototype)), _class);
  function tapPromise(proxy, promise) {
    proxy.isPending = true;
    proxy.isSettled = false;
    proxy.isFulfilled = false;
    proxy.isRejected = false;
    return rsvp.resolve(promise).then(content => {
      proxy.isPending = false;
      proxy.isFulfilled = true;
      proxy.isSettled = true;
      proxy.content = content;
      return content;
    }, error => {
      proxy.isPending = false;
      proxy.isFulfilled = false;
      proxy.isRejected = true;
      proxy.isSettled = true;
      throw error;
    });
  }
  const EmberObjectMethods = ['addObserver', 'cacheFor', 'decrementProperty', 'get', 'getProperties', 'incrementProperty', 'notifyPropertyChange', 'removeObserver', 'set', 'setProperties', 'toggleProperty'];
  EmberObjectMethods.forEach(method => {
    PromiseManyArray.prototype[method] = function delegatedMethod() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return Ember__default["default"][method](this, ...args);
    };
  });
  const InheritedProxyMethods = ['addArrayObserver', 'addObject', 'addObjects', 'any', 'arrayContentDidChange', 'arrayContentWillChange', 'clear', 'compact', 'every', 'filter', 'filterBy', 'find', 'findBy', 'getEach', 'includes', 'indexOf', 'insertAt', 'invoke', 'isAny', 'isEvery', 'lastIndexOf', 'map', 'mapBy', 'objectAt', 'objectsAt', 'popObject', 'pushObject', 'pushObjects', 'reduce', 'reject', 'rejectBy', 'removeArrayObserver', 'removeAt', 'removeObject', 'removeObjects', 'replace', 'reverseObjects', 'setEach', 'setObjects', 'shiftObject', 'slice', 'sortBy', 'toArray', 'uniq', 'uniqBy', 'unshiftObject', 'unshiftObjects', 'without'];
  InheritedProxyMethods.forEach(method => {
    PromiseManyArray.prototype[method] = function proxiedMethod() {
      return this.content[method](...arguments);
    };
  });

  /* 
      In case someone defined a relationship to a mixin, for example:
      ```
        import Model, { belongsTo, hasMany } from '@ember-data/model';
        import Mixin from '@ember/object/mixin';

        class CommentModel extends Model {
          @belongsTo('commentable', { polymorphic: true }) owner;
        }

        let Commentable = Mixin.create({
          @hasMany('comment') comments;
        });
      ```
      we want to look up a Commentable class which has all the necessary
      relationship meta data. Thus, we look up the mixin and create a mock
      Model, so we can access the relationship CPs of the mixin (`comments`)
      in this case
    */
  function modelForMixin(store, normalizedModelName) {
    let owner = application.getOwner(store);
    let MaybeMixin = owner.factoryFor(`mixin:${normalizedModelName}`);
    let mixin = MaybeMixin && MaybeMixin.class;
    if (mixin) {
      let ModelForMixin = Model.extend(mixin);
      ModelForMixin.reopenClass({
        __isMixin: true,
        __mixin: mixin
      });
      //Cache the class as a model
      owner.register('model:' + normalizedModelName, ModelForMixin);
    }
    return owner.factoryFor(`model:${normalizedModelName}`);
  }

  exports.Errors = Errors;
  exports.ManyArray = manyArray;
  exports.Model = Model;
  exports.PromiseBelongsTo = PromiseBelongsTo;
  exports.PromiseManyArray = PromiseManyArray;
  exports._modelForMixin = modelForMixin;
  exports.attr = attr$1;
  exports.belongsTo = belongsTo$1;
  exports.diffArray = diffArray;
  exports.hasMany = hasMany$1;

  Object.defineProperty(exports, '__esModule', { value: true });

}));
